<template>
	<div>
		<div
			id="mouseover"
			@mouseenter="actions.setConstrain"
			@mousemove="actions.onMouseMove"
			@mouseleave="actions.resetConstrain">
			<div
				class="sticker sticker-card sticker-tts"
				:class="hover && 'sticker-card-hover pointer'"
				:id="'sticker-card' + id"
				@click="actions.onStickerClick()">
				<!-- Favorite icon -->
				<div
					class="favorite-icon d-flex flex-column justify-center align-center"
					@click.stop="actions.toggleFavorite($event, sticker)">
					<img :src="actions.getFavoriteIcon()" alt="Favorite" />
				</div>

				<div class="icons d-flex flex-column">
					<img src="@/assets/icons/icon_tts.png" class="mt-1" alt="TTS" />
					<img v-if="sticker.subOnly" src="@/assets/icons/icon_subonly.png" class="mt-1" alt="Sub Only" />
					<img v-if="sticker.isStickerPlacementEnabled" src="@/assets/icons/icon_placement.png" class="mt-1" alt="Custom Placement" />
				</div>

				<!-- <div class="tts-chip" :class="theme === 'blue' ? 'darkblue-chip' : 'blue-chip'">TTS</div> -->

				<!-- Set Sticker Image Assets -->

				<AssetsTTS :sticker="sticker" :list="true" />

				<div class="text-center sticker-name white--text fs--12 pt-2">{{  sticker.name.length > 15 ? `${sticker.name.substr(0, 15)}...` : sticker.name }}</div>

				<div v-if="!isPartyModeOn" class="sticker-footer d-flex align-center justify-center">
					<img :src="actions.getIcon(bitsValue)" width="15" class="mt-1" alt="Bits" />
					<span class="white--text fs--14 fw--500 mt-1 pl-1">{{ bitsValue }}</span>
				</div>
				<div v-else class="sticker-footer d-flex align-center justify-center">
					<span class="white--text fs--14 fw--500 mt-1 pl-1">Free!</span>
				</div>

				<!-- <div class="sound-preview-btn px-1" :class="[isMobile && 'visible']" :id="`preview-${id}`">
					<v-icon size="20" color="white">{{ icons.mdiPlayCircle }}</v-icon>
				</div> -->
			</div>
		</div>

		<TTSDialog
			v-if="ttsDialog && clickedSticker && clickedSticker.id === sticker.id"
			:ttsDialog="ttsDialog"
			:sticker="clickedSticker"
			@useSticker="actions.onUseSticker"
			@close="actions.closeDialog" />
	</div>
</template>

<script>
import { ref, computed, watch } from 'vue';
import { useStore } from '@/store';
import _cloneDeep from 'lodash/cloneDeep';
import { getBitIcon } from '@/utils/utils';
import { StickerType } from '@/utils/constants';
import TTSDialog from '@/views/panels/TTSDialog.vue';
import AssetsTTS from './AssetsTTS.vue';
import { mdiPlayCircle } from '@mdi/js'

export default {
	name: 'StickerTTS',
	props: {
		id: String,
		sticker: Object,
	},
	components: {
		AssetsTTS,
		TTSDialog,
	},
	emits: ['onStickerClick', 'onAddFavorite', 'onDeleteFavorite'],
	setup(props, ctx) {
		const ttsDialog = ref(false);
		const hover = ref(false);
		const constrain = ref(0);

		const clickedSticker = ref(null);

		const store = useStore();

		const theme = computed(() => store.state.theme);
		const isMobile = computed(() => {
			return store.state.isMobile;
		});

		const bitsValue = computed(() => {
			if (props.sticker.bits && props.sticker.bits !== 0) {
				return props.sticker.bits
					? props.sticker.bits.toLocaleString()
					: Number(props.sticker.sku.split('_')[1]).toLocaleString();
			}
			return 0;
		});

		const speed = computed(() => store.state.speed);
		const isPartyModeOn = computed(() => store.state.isPartyModeOn);

		watch(
			() => speed,
			function (val) {
				constrain.value = val;
			},
		);

		const isFavorite = computed(() => {
			// Global sticker ids are stored with the numeric id since they can be multiple sizes.
			let stickerId;
			const s = props.sticker;
			if (s.stickerType != StickerType.GLOBAL) {
				stickerId = s.id;
			} else {
				stickerId = s.id.split('_')[2];
			}

			return store.state.favoriteStickers.some(fav => fav.id === stickerId);
		});

		// See store index.js for flow details on the status check call before showing the TTS text dialog.
		const isTtsStatusCheckSuccess = computed(() => store.state.isTtsStatusCheckSuccess);
		watch(isTtsStatusCheckSuccess, () => {
			if (isTtsStatusCheckSuccess.value) {
				const stickerObj = { ...store.state.clickedTtsSticker };
				clickedSticker.value = { ...stickerObj };
				ttsDialog.value = true;
				store.commit('setIsDoTtsStatusCheckOnClick', false);
			} else {
				store.commit('setClickedTtsSticker', null);
				clickedSticker.value = null;
				ttsDialog.value = false;
			}
		});

		const actions = {
			getFavoriteIcon() {
				if (isFavorite.value) {
					return require(`@/assets/icons/favorite/heart.png`);
				}

				return require(`@/assets/icons/favorite/${theme.value}.png`);
			},

			toggleFavorite(event, sticker) {
				const data = {
					id: sticker.id,
					stickerType: sticker.stickerType,
				};

				// Add in Favorites or Remove from favorites
				if (!isFavorite.value) {
					ctx.emit('onAddFavorite', data);
				} else {
					ctx.emit('onDeleteFavorite', data);
				}

				event.stopPropagation(); // Prevent sticker slap event.
			},
			onStickerClick() {
				// Set values to control parent flow. See store index.js for flow details.
				store.commit('setIsTtsStatusCheckSuccess', false);
				store.commit('setIsDoTtsStatusCheckOnClick', true);
				store.commit('setClickedTtsSticker', null);

				// Emit the event so the parent does the sticker status check.
				ctx.emit('onStickerClick', props.sticker);

				clickedSticker.value = { ...props.sticker };

				store.dispatch('saveAnalytic', {
					event: 'sticker_clicked',
					category: 'engagement',
					label: 'user_clicked_sticker',
				});
			},
			onUseSticker(text) {
				// This is an actual sticker slap, so turn off the status-check-only flag.
				store.commit('setIsDoTtsStatusCheckOnClick', false);

				const cloned = _cloneDeep(props.sticker);
				cloned.tts.text = text;
				ctx.emit('onStickerClick', cloned);
				actions.closeDialog();
			},
			closeDialog() {
				clickedSticker.value = null;
				ttsDialog.value = false;
			},
			setConstrain() {
				if (!isMobile.value) {
					hover.value = true;
					constrain.value = speed.value;
				}
			},
			getIcon(value) {
				let bits = value;
				if (typeof bits === 'string') {
					bits = Number(bits.replaceAll(',', ''));
				}

				return getBitIcon(bits);
			},
			resetConstrain() {
				hover.value = false;
				constrain.value = 0;

				let card = document.getElementById('sticker-card' + props.id);
				let xy = [0, 0];
				let position = xy.concat([card]);

				window.requestAnimationFrame(() => actions.transformElement(card, position));
			},
			transforms(x, y, el) {
				let box = el.getBoundingClientRect();
				let calcX = -(y - box.y - box.height / 2) / constrain.value;
				let calcY = (x - box.x - box.width / 2) / constrain.value;

				if (hover.value) {
					return (
						'perspective(100px) ' +
						'   rotateX(' +
						calcX +
						'deg) ' +
						'   rotateY(' +
						calcY +
						'deg) ' +
						'   scale(1.10) '
					);
				}

				return (
					'perspective(100px) ' + '   rotateX(' + 0 + 'deg) ' + '   rotateY(' + 0 + 'deg) ' + '   scale(1) '
				);
			},
			transformElement: function (el, xyEl) {
				if (!isMobile.value) {
					el.style.transform = this.transforms.apply(null, xyEl);
					el.style.transition = '0.25s ease-out';
				}
			},

			onMouseMove(e) {
				if (!isMobile.value) {
					hover.value = true;
					let card = document.getElementById('sticker-card' + props.id);
					let xy = [e.clientX, e.clientY];
					let position = xy.concat([card]);

					window.requestAnimationFrame(() => actions.transformElement(card, position));
				}
			},
		};

		return {
			isMobile,
			isFavorite,
			bitsValue,
			clickedSticker,
			ttsDialog,
			hover,
			constrain,
			theme,
			speed,
			actions,
			isPartyModeOn,
			icons: {
				mdiPlayCircle
			}
		};
	},
};
</script>


<style lang="scss" scoped>
.base-image {
	position: relative;
	margin: auto;
}
.sticker {
	overflow: hidden;
}
.sound-preview-btn {
	position: absolute;
	bottom: -50px;
	right: 3px;
	z-index: 4;
	border-radius: 4px;
	transition: bottom 0.3s ease;
}

.sticker:hover {
	.sound-preview-btn {
		bottom: 5px;
		right: 3px;
		transition: bottom 0.3s ease;
	}
}
	
.sticker:not(hover) {
	.sound-preview-btn {
		bottom: -50px;
		right: 3px;
		transition: bottom 0.3s ease;
	}
}

.visible {
	bottom: 5px !important;
}
</style>
